export const getUserType = user => {
  return user?.attributes?.profile?.publicData?.userType || null;
};

// helper: build city array from address
export const buildCityArray = (addr, selectedCity) => {
  if (!addr || typeof addr !== 'string') return [];

  // Split address by commas, trim whitespace, drop empty parts
  const parts = addr.split(',').map(s => s.trim()).filter(Boolean);
  if (!parts.length) return [];

  // Remove country name if 3+ parts (last part usually the country)
  if (parts.length >= 3) parts.pop();

  const result = [];

  // Last segment usually the main city or district (keep full phrase)
  const lastSeg = parts[parts.length - 1];
  if (lastSeg) {
    // If ends with " City" include both variations
    if (/\s*city$/i.test(lastSeg)) {
      const base = lastSeg.replace(/\s*city$/i, '').trim();
      if (base) result.push(base);
      result.push(lastSeg);
    } else {
      result.push(lastSeg);
    }
  }

  // Include the first segment if it looks meaningful (e.g., "Sector 17")
  const firstSeg = parts[0];
  if (firstSeg && firstSeg.length > 2) result.push(firstSeg);

  // Include selected city if available
  if (selectedCity) result.push(selectedCity);

  // Return unique entries (case-insensitive)
  const uniq = Array.from(
    new Set(result.map(s => s.trim()).filter(Boolean))
  ).filter(v => !/^\d+$/.test(v));

  return uniq;
};

export const extractCountryFromAddress = (address, publicCountry) => {
  // Priority: explicit publicCountry field if present

  if (!address) return null;

  // Split and trim parts
  const addressParts = address.split(',').map(p => p.trim()).filter(Boolean);
  if (addressParts.length === 0) return null;

  // If address ends with country-like token (last part), return it if length>2
  const lastPart = addressParts[addressParts.length - 1];
  if (lastPart && lastPart.length > 2) {
    // Basic heuristic: last part is often country
    return lastPart;
  }

  // Fallback: attempt to match a small set of common country names
  const commonCountries = ['India', 'United States', 'USA', 'United Kingdom', 'UK', 'Australia', 'Canada'];
  const match = commonCountries.find(c => address.toLowerCase().includes(c.toLowerCase()));
  return match || null;
};

// Helper function to extract city from address string
export const extractCityFromAddress = (address) => {
  if (!address) return null;

  // Split address by commas and clean up
  const addressParts = address.split(',').map(part => part.trim());

  // Common Indian cities for pattern matching
  const indianCities = [
    'Delhi', 'New Delhi', 'Mumbai', 'Bangalore', 'Bengaluru', 'Chennai', 'Kolkata',
    'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Kanpur',
    'Nagpur', 'Indore', 'Thane', 'Bhopal', 'Visakhapatnam', 'Pimpri-Chinchwad',
    'Surat', 'Vadodara', 'Rajkot', 'Patna', 'Agra', 'Nashik', 'Faridabad'
  ];

  // Look for known cities in the address parts
  for (const part of addressParts) {
    const cityMatch = indianCities.find(city =>
      part.toLowerCase().includes(city.toLowerCase()) ||
      city.toLowerCase().includes(part.toLowerCase())
    );
    if (cityMatch) {
      return cityMatch;
    }
  }

  // Specific pattern matching for common address formats
  const delhiMatch = address.match(/(New Delhi|Delhi Cantonment|Delhi)/i);
  if (delhiMatch) {
    return 'Delhi';
  }

  const mumbaiMatch = address.match(/(Mumbai|Bombay)/i);
  if (mumbaiMatch) {
    return 'Mumbai';
  }

  const bangaloreMatch = address.match(/(Bangalore|Bengaluru)/i);
  if (bangaloreMatch) {
    return 'Bangalore';
  }

  // If no specific city found, try to get from address structure
  if (addressParts.length >= 2) {
    const potentialCity = addressParts[1];
    if (potentialCity && potentialCity.length > 2) {
      return potentialCity;
    }
  }

  return null;
};